import { ArrowRight, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { Button } from "../../../components/custom/custom-button";
import TabLink from "../../../components/fragment/tab-link/TabLink";
import { RazorInputField } from "../../../components/razor-reuse";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import { getPromotionDetail, updatePromotion } from "../../../redux/promotion";
import RootState, { PromotionAudience, PromotionType } from "../../../redux/type";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { formatDateToYYYYMMDD } from "../../../utils/Helper";
import {
  AUDIENCE_OPTIONS,
  buildCampaignPayload,
  CAMPAIGN_TYPE_OPTIONS,
  CORRIDOR_CURRENCIES,
  createEmptyCorridors,
  getMinEndDate,
  mapPromotionToFormValues,
  REGIONS_Code,
  visibleCorridorCurrencies,
  type CorridorValue,
  type CurrencyValueRow,
} from "./campaignForm";
import MinTransactionAmountFields from "./components/MinTransactionAmountFields";
import NumberInput from "./components/NumberInput";
import RewardConfigCard from "./components/RewardConfigCard";

export const EDIT_PROMOTION_ID_KEY = "edit_promotion_id";

export default function EditCampaign() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    promotion_detail: promotion,
    loading_promotion_detail,
    loading_update_promotion,
  } = useSelector((state: RootState) => state.promotion);
  const { user } = useSelector((state: RootState) => state.auth);

  const [promotionId] = useState(() =>
    localStorage.getItem(EDIT_PROMOTION_ID_KEY),
  );

  const goToPromotions = () => {
    navigate(ALL_ROUTES?.DASHBOARD_REFERRAL, {
      state: { activeTab: "Promotions" },
    });
  };

  // redirect if user has no permission
  useEffect(() => {
    if (
      !user?.permissions?.includes("edit_promotion") &&
      user?.permissions?.length > 0
    ) {
      navigate(ALL_ROUTES.DASHBOARD_REFERRAL);
    }
  }, [user?.permissions]);

  useEffect(() => {
    if (!promotionId) {
      goToPromotions();
      return;
    }
    dispatch(getPromotionDetail(promotionId) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [campaignName, setCampaignName] = useState("");
  const [description, setDescription] = useState("");
  const [campaignType, setCampaignType] =
    useState<PromotionType>("cashback_percent");
  const [minTransactionAmounts, setMinTransactionAmounts] = useState<
    CurrencyValueRow[]
  >([]);
  const [rewardConfigs, setRewardConfigs] = useState<CurrencyValueRow[]>([]);
  const [eligibleRegions, setEligibleRegions] = useState<string[]>([]);
  const [corridors, setCorridors] = useState<Record<string, CorridorValue>>(
    createEmptyCorridors,
  );
  const [usageLimitTotal, setUsageLimitTotal] = useState("");
  const [usageLimitPerUser, setUsageLimitPerUser] = useState("");
  const [audience, setAudience] = useState<PromotionAudience>("all_users");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!promotion) return;
    const values = mapPromotionToFormValues(promotion);
    setCampaignName(values.campaignName);
    setDescription(values.description);
    setCampaignType(values.campaignType);
    setMinTransactionAmounts(values.minTransactionAmounts);
    setRewardConfigs(values.rewardConfigs);
    setEligibleRegions(values.eligibleRegions);
    setCorridors(values.corridors);
    setUsageLimitTotal(values.usageLimitTotal);
    setUsageLimitPerUser(values.usageLimitPerUser);
    setAudience(values.audience);
    setStartDate(values.startDate);
    setEndDate(values.endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotion]);

  const isAllUsersAudience = audience === "all_users";
  const visibleCorridors = visibleCorridorCurrencies(eligibleRegions);
  const isCorridorFiltered = visibleCorridors.length < CORRIDOR_CURRENCIES.length;

  useEffect(() => {
    if (isAllUsersAudience && usageLimitTotal !== "") {
      setUsageLimitTotal("");
    }
  }, [isAllUsersAudience, usageLimitTotal]);

  useEffect(() => {
    const visibleCodes = visibleCorridors.map((c) => c.code);
    setCorridors((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((code) => {
        if (
          !visibleCodes.includes(code) &&
          (next[code].outbound || next[code].inbound)
        ) {
          next[code] = { outbound: false, inbound: false };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleRegions]);

  useEffect(() => {
    if (startDate && endDate && endDate <= startDate) {
      setEndDate("");
    }
  }, [startDate, endDate]);

  const tabLinkList = [
    { label: "Overview", active: false },
    { label: "Referral Settings", active: false },
    { label: "Promotions", active: true },
    { label: "Influencer Programme", active: false },
  ];

  const toggleRegion = (region: string) => {
    setEligibleRegions((prev) =>
      prev.includes(region)
        ? prev.filter((r) => r !== region)
        : [...prev, region],
    );
  };

  const toggleCorridor = (code: string, direction: keyof CorridorValue) => {
    setCorridors((prev) => ({
      ...prev,
      [code]: { ...prev[code], [direction]: !prev[code][direction] },
    }));
  };

  const handleUpdateCampaign = async () => {
    if (!promotion) return;

    const { status, ...updatePayload } = buildCampaignPayload(
      {
        campaignName,
        description,
        campaignType,
        minTransactionAmounts,
        rewardConfigs,
        eligibleRegions,
        corridors,
        usageLimitTotal,
        usageLimitPerUser,
        audience,
        startDate,
        endDate,
      },
      promotion.status,
    );

    const result: any = await dispatch(
      updatePromotion({ id: promotion.id, payload: updatePayload }) as any,
    );
    if (result?.payload?.data?.status) {
      goToPromotions();
    }
  };

  const isInitialLoad = loading_promotion_detail && !promotion;

  if (isInitialLoad) {
    return (
      <DashboardLayoutMain
        title="Referral Program"
        back
        onBack={goToPromotions}
      >
        <div className="mx-[2rem] py-[40px] space-y-[16px]">
          <Skeleton height={120} />
          <Skeleton height={200} />
          <Skeleton height={280} />
        </div>
      </DashboardLayoutMain>
    );
  }

  if (!promotion) return null;

  return (
    <DashboardLayoutMain title="Referral Program" back onBack={goToPromotions}>
      <section className="bg-white mx-[2rem] pt-[10px] space-y-4 mt-[15px]">
        <TabLink
          onClick={(param) => {
            if (param?.label === "Promotions") {
              goToPromotions();
            } else {
              navigate(ALL_ROUTES?.DASHBOARD_REFERRAL, {
                state: { activeTab: param?.label },
              });
            }
          }}
          list={tabLinkList}
          className="single-customer-tab-link"
        />
      </section>

      <div className="mx-[2rem] py-[16px] space-y-[20px] max-w-[900px]">
        <section>
          <p className="text-[20px] font-bold text-[#1E1E1E]">Edit Campaign</p>
          <p className="text-[14px] text-[#758089] mt-[4px]">
            Update settings for {promotion.name}
          </p>
        </section>

        {/* campaign information */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Campaign Information
          </p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            Basic details about campaign
          </p>

          <div className="mt-[20px]">
            <label className="text-[14px] text-[#758089]">Campaign Name</label>
            <input
              type="text"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
            />
          </div>

          <div className="mt-[16px]">
            <label className="text-[14px] text-[#758089]">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-[8px] w-full resize-none rounded-[8px] border border-[#E4E4E7] px-[12px] py-[10px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
            />
          </div>
        </section>

        {/* campaign type */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">Campaign Type</p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            Choose how this campaign rewards users and its value
          </p>

          <div className="mt-[20px] max-w-[360px]">
            <label className="text-[14px] text-[#758089]">Type</label>
            <select
              value={campaignType}
              onChange={(e) =>
                setCampaignType(e.target.value as PromotionType)
              }
              className="mt-[8px] h-[40px] w-full appearance-none rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
            >
              {CAMPAIGN_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <MinTransactionAmountFields
            rows={minTransactionAmounts}
            onChange={setMinTransactionAmounts}
          />
        </section>

        <RewardConfigCard
          rows={rewardConfigs}
          onChange={setRewardConfigs}
          campaignType={campaignType}
        />

        {/* eligibility */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">Eligibility</p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            Define which regions and corridors qualify for this campaign
          </p>

          <p className="mt-[20px] text-[14px] font-medium text-[#1E1E1E]">
            Eligible Regions
          </p>
          <div className="mt-[10px] flex flex-wrap gap-[10px]">
            {REGIONS_Code.map((region) => {
              const active = eligibleRegions.includes(region.value);
              return (
                <button
                  key={region.value}
                  type="button"
                  onClick={() => toggleRegion(region.value)}
                  className={`rounded-full border px-[20px] py-[8px] text-[14px] ${
                    active
                      ? "border-[#734792] text-[#734792]"
                      : "border-[#E4E4E7] text-[#3F3F46]"
                  }`}
                >
                  {region.label}
                </button>
              );
            })}
          </div>

          <p className="mt-[24px] text-[14px] font-medium text-[#1E1E1E]">
            Eligible Corridors
          </p>
          {isCorridorFiltered && (
            <p className="mt-[2px] text-[13px] text-[#A1A1AA]">
              Only showing corridors for the selected regions
            </p>
          )}
          {visibleCorridors.length === 0 ? (
            <p className="mt-[10px] text-[13px] text-[#A1A1AA]">
              None of the selected regions have a corridor available
            </p>
          ) : (
          <div className="mt-[10px] grid grid-cols-1 sm:grid-cols-2 gap-[24px]">
            <div>
              <p className="text-[12px] font-medium tracking-wide text-[#A1A1AA]">
                OUTBOUND
              </p>
              <div className="mt-[8px] space-y-[8px]">
                {visibleCorridors.map(({ code }) => (
                  <button
                    key={`out-${code}`}
                    type="button"
                    onClick={() => toggleCorridor(code, "outbound")}
                    className={`flex w-full items-center justify-between rounded-[8px] border px-[14px] py-[10px] ${
                      corridors[code].outbound
                        ? "border-[#734792]"
                        : "border-[#E4E4E7]"
                    }`}
                  >
                    <span className="flex items-center gap-[8px] text-[14px] text-[#1E1E1E]">
                      NGN <ArrowRight className="size-[14px] text-[#A1A1AA]" />{" "}
                      {code}
                    </span>
                    <span
                      className={`flex size-[18px] items-center justify-center rounded-[4px] border ${
                        corridors[code].outbound
                          ? "border-[#734792] bg-[#734792] text-white"
                          : "border-[#D4D4D8] bg-white"
                      }`}
                    >
                      {corridors[code].outbound && (
                        <Check className="size-[12px]" />
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[12px] font-medium tracking-wide text-[#A1A1AA]">
                INBOUND
              </p>
              <div className="mt-[8px] space-y-[8px]">
                {visibleCorridors.map(({ code }) => (
                  <button
                    key={`in-${code}`}
                    type="button"
                    onClick={() => toggleCorridor(code, "inbound")}
                    className={`flex w-full items-center justify-between rounded-[8px] border px-[14px] py-[10px] ${
                      corridors[code].inbound
                        ? "border-[#734792]"
                        : "border-[#E4E4E7]"
                    }`}
                  >
                    <span className="flex items-center gap-[8px] text-[14px] text-[#1E1E1E]">
                      {code}{" "}
                      <ArrowRight className="size-[14px] text-[#A1A1AA]" /> NGN
                    </span>
                    <span
                      className={`flex size-[18px] items-center justify-center rounded-[4px] border ${
                        corridors[code].inbound
                          ? "border-[#734792] bg-[#734792] text-white"
                          : "border-[#D4D4D8] bg-white"
                      }`}
                    >
                      {corridors[code].inbound && (
                        <Check className="size-[12px]" />
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}
        </section>

        {/* usage & audience */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Usage &amp; Audience
          </p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            Control how many times this campaign can be redeemed and who it is
            available to
          </p>

          <div className="mt-[20px] max-w-[360px]">
            <label className="text-[14px] text-[#758089]">Audience</label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value as PromotionAudience)}
              className="mt-[8px] h-[40px] w-full appearance-none rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
            >
              {AUDIENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-[8px] text-[13px] text-[#A1A1AA]">
              Who is eligible to see and redeem this campaign
            </p>
          </div>

          <div className="mt-[20px] grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
            <div>
              <label className="text-[14px] text-[#758089]">
                Total Users Allowed
              </label>
              <NumberInput
                min={1}
                value={usageLimitTotal}
                onChange={(e) => setUsageLimitTotal(e.target.value)}
                disabled={isAllUsersAudience}
                placeholder={isAllUsersAudience ? "Not applicable" : "Unlimited"}
                className={`mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] outline-none focus:border-[#734792] ${
                  isAllUsersAudience
                    ? "bg-[#F4F4F5] text-[#A1A1AA]"
                    : "text-[#1E1E1E]"
                }`}
              />
              <p className="mt-[8px] text-[13px] text-[#A1A1AA]">
                {isAllUsersAudience
                  ? "Not applicable for the All Users audience — total participants aren't capped"
                  : "Maximum number of users who can redeem this campaign in total. Leave blank for unlimited"}
              </p>
            </div>
            <div>
              <label className="text-[14px] text-[#758089]">
                Redemptions Per User
              </label>
              <NumberInput
                min={1}
                value={usageLimitPerUser}
                onChange={(e) => setUsageLimitPerUser(e.target.value)}
                placeholder="Unlimited"
                className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
              />
              <p className="mt-[8px] text-[13px] text-[#A1A1AA]">
                How many times a single user can use this campaign. Leave
                blank for unlimited
              </p>
            </div>
          </div>
        </section>

        {/* validity */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">Validity</p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            Set when this campaign starts and ends
          </p>

          <div className="mt-[20px] grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
            <RazorInputField
              color="black-light"
              type="date"
              label="Start Date"
              placeholder=" Select date"
              value={startDate}
              onChange={(date: any) =>
                setStartDate(formatDateToYYYYMMDD(date[0]))
              }
            />
            <RazorInputField
              color="black-light"
              type="date"
              label="End Date"
              placeholder=" Select date"
              value={endDate}
              onChange={(date: any) =>
                setEndDate(formatDateToYYYYMMDD(date[0]))
              }
              dateOptions={{ minDate: getMinEndDate(startDate) }}
              disabled={!startDate}
            />
          </div>
        </section>

        {/* actions */}
        <section className="flex items-center gap-[12px] pb-[40px]">
          <Button
            variant="ghost"
            onClick={goToPromotions}
            disabled={loading_update_promotion}
            className="bg-[#FDECEC] text-[#DC2626] hover:bg-[#FDECEC] hover:text-[#DC2626]"
          >
            Cancel
          </Button>
          <Button
            isLoading={loading_update_promotion}
            onClick={handleUpdateCampaign}
          >
            Update Campaign
          </Button>
        </section>
      </div>
    </DashboardLayoutMain>
  );
}
